/**
 * FormSection
 * Groups form controls under a labelled, optionally collapsible section.
 * Uses the FormSection schema type for section metadata.
 */

import React, { useState } from 'react';
import type { FormSection as FormSectionSchema, FormControl } from '@atlas-forms/types-js';

export interface FormSectionProps {
  section: FormSectionSchema;
  children: React.ReactNode;
  /** Controls in this section (for aria-labelledby / empty state) */
  controlCount?: number;
}

export function FormSection({ section, children, controlCount = 0 }: FormSectionProps) {
  const [expanded, setExpanded] = useState(section.defaultExpanded !== false);
  const headingId = `section-heading-${section.id}`;

  if (controlCount === 0) return null;

  return (
    <section
      className="form-section"
      aria-labelledby={headingId}
      data-section-id={section.id}
    >
      <div className="form-section__header">
        <h3 id={headingId} className="form-section__title">
          {section.title}
        </h3>
        {section.description && (
          <p className="form-section__description">{section.description}</p>
        )}
        {section.collapsible && (
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
            className="form-section__toggle"
          >
            {expanded ? 'Collapse' : 'Expand'}
          </button>
        )}
      </div>
      {expanded && (
        <div className="form-section__controls">{children}</div>
      )}
    </section>
  );
}
